import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  PhoneIcon,
  CheckCircleIcon,
  HomeIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import LocalCitySeoBlock from '../components/LocalCitySeoBlock';
import { LocalCitySeo } from '../data/localSeoContent';

interface PhonePlan {
  id: number;
  name: string;
  price: number;
  setupFee: number;
  features: string[];
  popular?: boolean;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

const emptyPortForm = {
  numbersToPort: '',
  secondaryLines: '',
  previousProvider: '',
  longDistanceProvider: '',
  customerBillingName: '',
  accountNumber: '',
  accountPin: '',
  installationAddress: '',
  mailingAddress: '',
  alternateContact: '',
  typedSignatureName: '',
  signatureDate: new Date().toISOString().slice(0, 10),
  authorizationAccepted: false
};

const fieldClass =
  'w-full rounded-lg border border-white/15 bg-white/5 px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500';
const labelClass = 'block text-sm font-semibold text-white mb-1';
const hintClass = 'text-xs text-white/45 mb-2';

export type PhoneServicePageProps = {
  city?: string;
  heroTitle?: React.ReactNode;
  heroSubtitle?: string;
  localSeo?: LocalCitySeo;
};

const PhoneService: React.FC<PhoneServicePageProps> = ({
  city,
  heroTitle,
  heroSubtitle =
    'Everyone needs awesome phone service. Get reliable, clear voice service with features that matter for your home or business.',
  localSeo,
}) => {
  const [portModalOpen, setPortModalOpen] = useState(false);
  const [portFormSubmitting, setPortFormSubmitting] = useState(false);
  const [portFormStatus, setPortFormStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [portFile, setPortFile] = useState<File | null>(null);
  const [portForm, setPortForm] = useState(emptyPortForm);
  const [signatureDataUrl, setSignatureDataUrl] = useState('');
  const signatureCanvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const id = 'great-vibes-font';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;

    const draw = async () => {
      const name = portForm.typedSignatureName.trim();
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const cssW = canvas.clientWidth || 560;
      const cssH = canvas.clientHeight || 120;
      canvas.width = Math.floor(cssW * ratio);
      canvas.height = Math.floor(cssH * ratio);
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      ctx.clearRect(0, 0, cssW, cssH);

      // Signature guide line
      ctx.strokeStyle = 'rgba(11,107,203,0.18)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(12, cssH * 0.72);
      ctx.lineTo(cssW - 12, cssH * 0.72);
      ctx.stroke();

      if (!name) {
        setSignatureDataUrl('');
        return;
      }

      try {
        await (document as Document & { fonts?: FontFaceSet }).fonts?.load('48px "Great Vibes"');
      } catch {
        // Fallback fonts below still work if Great Vibes is unavailable.
      }

      ctx.fillStyle = '#142033';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      let size = Math.min(64, Math.floor(cssW / Math.max(name.length * 0.42, 6)));
      size = Math.max(28, size);
      const fontStack = '"Great Vibes", "Segoe Script", "Brush Script MT", cursive';
      ctx.font = `${size}px ${fontStack}`;
      while (ctx.measureText(name).width > cssW - 36 && size > 22) {
        size -= 2;
        ctx.font = `${size}px ${fontStack}`;
      }
      ctx.fillText(name, cssW / 2, cssH * 0.52);
      setSignatureDataUrl(canvas.toDataURL('image/png'));
    };

    void draw();
  }, [portForm.typedSignatureName, portModalOpen]);

  const handlePortFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    if (portFormStatus) setPortFormStatus(null);
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setPortForm((prev) => ({ ...prev, [name]: checked }));
      return;
    }
    setPortForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePortFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!portForm.authorizationAccepted) {
      setPortFormStatus({
        type: 'error',
        message: 'You must accept the authorization statement before submitting.'
      });
      return;
    }
    if (!portForm.typedSignatureName.trim() || !signatureDataUrl) {
      setPortFormStatus({
        type: 'error',
        message: 'Type your full legal name to create your electronic signature.'
      });
      return;
    }
    if (!portFile) {
      setPortFormStatus({
        type: 'error',
        message: 'Please upload a copy of your phone bill (PDF, JPG, or PNG).'
      });
      return;
    }

    setPortFormSubmitting(true);
    setPortFormStatus(null);

    try {
      const formData = new FormData();
      Object.entries(portForm).forEach(([key, value]) => {
        formData.append(key, typeof value === 'boolean' ? (value ? '1' : '0') : String(value));
      });
      formData.append('signaturePng', signatureDataUrl);
      formData.append('signatureMethod', 'typed');
      if (portFile) formData.append('phoneBill', portFile);

      const response = await fetch('/api/port-number-request.php', {
        method: 'POST',
        body: formData
      });

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        const responseText = await response.text();
        throw new Error(
          responseText.trim().startsWith('<!DOCTYPE') || responseText.trim().startsWith('<html')
            ? 'Local preview is hitting the React dev server instead of the PHP API. This form must be tested on a PHP-enabled server.'
            : 'The server returned an unexpected response while sending the port request.'
        );
      }

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Unable to send port request');
      }

      setPortFormStatus({
        type: 'success',
        message:
          'Your Local TN LOA was sent successfully' +
          (portFile ? ' with your phone bill attached.' : '. Please also email a copy of your bill to airmaxhelpdesk@gmail.com if you did not attach one.')
      });

      setPortForm({
        ...emptyPortForm,
        signatureDate: new Date().toISOString().slice(0, 10)
      });
      setPortFile(null);
      setSignatureDataUrl('');
    } catch (error) {
      setPortFormStatus({
        type: 'error',
        message: error instanceof Error ? error.message : 'Unable to send port request right now.'
      });
    } finally {
      setPortFormSubmitting(false);
    }
  };

  const phonePlans: PhonePlan[] = [
    {
      id: 1,
      name: 'Basic',
      price: 19.99,
      setupFee: 10.0,
      icon: HomeIcon,
      features: ['Unlimited Calling', 'Local & Long Distance', 'Voicemail', 'Caller ID', 'Call Waiting'],
      popular: false
    },
    {
      id: 2,
      name: 'Plus',
      price: 24.99,
      setupFee: 10.0,
      icon: HomeIcon,
      features: [
        'Unlimited Calling',
        'Local & Long Distance',
        'Voicemail',
        'Caller ID',
        'Call Waiting',
        'Call Forwarding',
        '3-Way Calling'
      ],
      popular: true
    },
    {
      id: 3,
      name: 'Pro',
      price: 34.99,
      setupFee: 15.0,
      icon: HomeIcon,
      features: [
        'Unlimited Calling',
        'Local & Long Distance',
        'Voicemail',
        'Caller ID',
        'Call Waiting',
        'Call Forwarding',
        '3-Way Calling',
        'Business Features'
      ],
      popular: false
    },
    {
      id: 4,
      name: 'Business',
      price: 49.99,
      setupFee: 20.0,
      icon: BuildingOfficeIcon,
      features: [
        'Unlimited Calling',
        'Local & Long Distance',
        'Voicemail',
        'Caller ID',
        'Call Waiting',
        'Call Forwarding',
        '3-Way Calling',
        'Advanced Business Features',
        'Priority Support'
      ],
      popular: false
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a0f1a] pt-20">
      <section className="relative py-12 md:py-16 bg-gradient-to-br from-airmax-blue to-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6">
              {heroTitle ?? (
                <>
                  Home and Business
                  <br />
                  <span className="text-white">Phone Service</span>
                </>
              )}
            </h1>
            <p className="text-lg md:text-2xl text-blue-100 max-w-3xl mx-auto">{heroSubtitle}</p>
          </motion.div>
        </div>
      </section>

      <section className="py-12 md:py-20 bg-[#111827]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Choose Your Phone Plan</h2>
            <p className="text-xl text-white/60 max-w-3xl mx-auto">
              Affordable phone service plans with unlimited calling and great features.
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-10 rounded-2xl border border-green-400/30 bg-green-500/10 shadow-sm p-6 md:p-8"
          >
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="max-w-3xl">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-green-500/15 border border-green-400/30 text-green-200 text-sm font-semibold mb-3">
                  Already a Customer?
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">Access Your Phone Portal</h3>
                <p className="text-white/65 mb-4">
                  Access your phone account online — listen to your voicemail messages and view your call history
                  anytime, from any device.
                </p>
                <ul className="flex flex-wrap gap-3 text-sm text-white/65">
                  <li className="flex items-center gap-1">
                    <CheckCircleIcon className="h-4 w-4 text-green-400" /> Voicemail access
                  </li>
                  <li className="flex items-center gap-1">
                    <CheckCircleIcon className="h-4 w-4 text-green-400" /> Call history
                  </li>
                </ul>
              </div>
              <div className="flex-shrink-0">
                <a
                  href="https://airmaxpbx.com/client_portal/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-4 rounded-xl bg-green-600 text-white font-semibold hover:bg-green-500 transition-colors shadow-md"
                >
                  <PhoneIcon className="h-5 w-5 mr-2" />
                  Go to Phone Portal
                </a>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-10 rounded-2xl border border-blue-400/30 bg-blue-500/10 shadow-sm p-6 md:p-8"
          >
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="max-w-3xl">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-500/15 border border-blue-400/30 text-blue-300 text-sm font-semibold mb-3">
                  Keep Your Current Phone Number
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">Need to Port Your Number?</h3>
                <p className="text-white/65 mb-4">
                  Complete the Local TN LOA (Letter of Authorization). Details must match your previous provider bill
                  exactly. You&apos;ll type your name to create an electronic signature and can attach your phone bill.
                </p>
                <p className="text-sm text-white/45">
                  Have your most recent phone bill ready (PDF, JPG, or PNG). You must keep service active with your
                  previous provider until the port is complete.
                </p>
              </div>

              <div className="flex-shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setPortFormStatus(null);
                    setPortModalOpen(true);
                  }}
                  className="inline-flex items-center justify-center px-6 py-4 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-500 transition-colors shadow-md"
                >
                  <DocumentTextIcon className="h-5 w-5 mr-2" />
                  Open Local TN LOA
                </button>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {phonePlans.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`bg-[#0a0f1a] rounded-2xl border border-white/10 shadow-lg shadow-black/30 card-hover relative ${
                  plan.popular ? 'ring-2 ring-blue-500 scale-105' : ''
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                    <span className="bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="p-8">
                  <div className="flex items-center justify-center mb-4">
                    <div className="w-12 h-12 rounded-full bg-blue-500/15 flex items-center justify-center">
                      <plan.icon className="h-6 w-6 text-blue-300" />
                    </div>
                  </div>

                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                    <div className="text-4xl font-bold text-blue-400 mb-2">
                      ${plan.price}
                      <span className="text-lg text-white/50">/month</span>
                    </div>
                    <p className="text-sm text-white/45 mt-1">Taxes may apply</p>
                    <p className="text-sm text-orange-400 font-medium mt-2">
                      ${plan.setupFee} {plan.setupFee === 10 ? 'Activation' : 'Setup'} Fee
                    </p>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start">
                        <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-white/65 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <a
                    href="tel:9285656707"
                    className="w-full text-center flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-semibold transition-all bg-blue-600 text-white hover:bg-blue-500 shadow-md"
                  >
                    <PhoneIcon className="h-5 w-5" />
                    Call To Order
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {localSeo && <LocalCitySeoBlock content={localSeo} />}

      <section className="py-20 bg-[#0a0f1a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Why Choose Airmax Phone Service?</h2>
            <p className="text-xl text-white/60 max-w-3xl mx-auto">
              Reliable phone service with features that keep you connected.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-blue-500/15 flex items-center justify-center">
                  <PhoneIcon className="h-8 w-8 text-blue-300" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Crystal Clear Quality</h3>
              <p className="text-white/65">
                Experience clear voice quality with our advanced digital phone service technology.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-center"
            >
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-blue-500/15 flex items-center justify-center">
                  <CheckCircleIcon className="h-8 w-8 text-blue-300" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Unlimited Calling</h3>
              <p className="text-white/65">
                Call anyone, anywhere in the US without worrying about minutes or long-distance charges.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-center"
            >
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-blue-500/15 flex items-center justify-center">
                  <HomeIcon className="h-8 w-8 text-blue-300" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Local Support</h3>
              <p className="text-white/65">
                Arizona-based customer service team ready to help you with any phone service needs.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-r from-airmax-blue to-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Get Connected?</h2>
            <p className="text-xl text-blue-100 mb-6 max-w-2xl mx-auto">
              Contact us today to get started with Airmax Phone Service.
            </p>
            <a
              href="tel:928-565-6707"
              className="inline-flex items-center justify-center text-3xl md:text-4xl font-bold text-white hover:text-blue-100 transition-colors"
            >
              <PhoneIcon className="h-10 w-10 mr-3" />
              928-565-6707
            </a>
            <p className="text-blue-100/80 mt-3">Call us to order your phone service</p>
          </motion.div>
        </div>
      </section>

      {portModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setPortModalOpen(false)} />
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl bg-[#111827] border border-white/10 shadow-2xl"
          >
            <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-5 border-b border-white/10 bg-[#111827] rounded-t-2xl">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-white">Local TN LOA</h2>
                <p className="text-sm text-white/60 mt-1">
                  Letter of Authorization for Local Number Porting — must match your previous provider bill exactly.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPortModalOpen(false)}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              >
                <XMarkIcon className="h-6 w-6 text-white/70" />
              </button>
            </div>

            <div className="px-6 py-6">
              <div className="mb-6 rounded-xl border border-amber-400/30 bg-amber-500/10 p-4 space-y-2">
                <p className="text-sm font-semibold text-amber-200 uppercase tracking-wide">
                  Must be exactly as it appears on your bill from previous phone service provider
                </p>
                <p className="text-sm text-white/65">
                  Please send a copy of your bill showing Account Number, Billing Name, and Installation Address with
                  this completed form to help minimize delay.
                </p>
                <p className="text-sm font-semibold text-white">
                  YOU MUST MAINTAIN SERVICE WITH YOUR PREVIOUS PROVIDER UNTIL THE PORT PROCESS IS COMPLETE.
                </p>
              </div>

              {portFormStatus && (
                <div
                  className={`mb-6 rounded-xl border p-4 ${
                    portFormStatus.type === 'success'
                      ? 'border-green-400/30 bg-green-500/15 text-green-200'
                      : 'border-red-400/30 bg-red-500/15 text-red-200'
                  }`}
                >
                  <p className="text-sm font-medium">{portFormStatus.message}</p>
                </div>
              )}

              <form onSubmit={handlePortFormSubmit} className="space-y-8">
                <section className="space-y-4">
                  <h3 className="text-lg font-bold text-white border-b border-white/10 pb-2">
                    Numbers &amp; Providers
                  </h3>
                  <div>
                    <label className={labelClass}>Telephone Number(s) to be Ported *</label>
                    <p className={hintClass}>List each number. Specify if there are any secondary lines.</p>
                    <textarea
                      name="numbersToPort"
                      value={portForm.numbersToPort}
                      onChange={handlePortFormChange}
                      rows={3}
                      required
                      className={fieldClass}
                      placeholder="(928) 555-0100"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Secondary lines (if any)</label>
                    <input
                      name="secondaryLines"
                      value={portForm.secondaryLines}
                      onChange={handlePortFormChange}
                      className={fieldClass}
                      placeholder="Optional"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Previous Phone Service Provider *</label>
                      <p className={hintClass}>Also specify your long distance provider if different.</p>
                      <input
                        name="previousProvider"
                        value={portForm.previousProvider}
                        onChange={handlePortFormChange}
                        required
                        className={fieldClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Long Distance Provider</label>
                      <p className={hintClass}>If applicable / if different from local provider</p>
                      <input
                        name="longDistanceProvider"
                        value={portForm.longDistanceProvider}
                        onChange={handlePortFormChange}
                        className={fieldClass}
                      />
                    </div>
                  </div>
                </section>

                <section className="space-y-4">
                  <h3 className="text-lg font-bold text-white border-b border-white/10 pb-2">
                    Billing &amp; Addresses
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Customer Billing Name *</label>
                      <p className={hintClass}>Must be an authorized user on the previous provider account</p>
                      <input
                        name="customerBillingName"
                        value={portForm.customerBillingName}
                        onChange={handlePortFormChange}
                        required
                        className={fieldClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Account Number *</label>
                      <p className={hintClass}>From previous provider</p>
                      <input
                        name="accountNumber"
                        value={portForm.accountNumber}
                        onChange={handlePortFormChange}
                        required
                        className={fieldClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Account PIN / Passcode *</label>
                      <p className={hintClass}>PIN or passcode from your previous provider account</p>
                      <input
                        name="accountPin"
                        value={portForm.accountPin}
                        onChange={handlePortFormChange}
                        required
                        className={fieldClass}
                        autoComplete="off"
                      />
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Full Installation Address *</label>
                    <p className={hintClass}>Physical location of the phone; cannot be a PO Box</p>
                    <textarea
                      name="installationAddress"
                      value={portForm.installationAddress}
                      onChange={handlePortFormChange}
                      rows={2}
                      required
                      className={fieldClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Mailing Address</label>
                    <p className={hintClass}>If different from Installation Address</p>
                    <textarea
                      name="mailingAddress"
                      value={portForm.mailingAddress}
                      onChange={handlePortFormChange}
                      rows={2}
                      className={fieldClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Alternate Contact Information *</label>
                    <p className={hintClass}>Assigned temporary number, cell, or primary email</p>
                    <input
                      name="alternateContact"
                      value={portForm.alternateContact}
                      onChange={handlePortFormChange}
                      required
                      className={fieldClass}
                      placeholder="Cell, temp number, or email"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>
                      Upload phone bill * <span className="text-white/40 font-normal">(PDF, JPG, PNG)</span>
                    </label>
                    <p className={hintClass}>
                      Required — bill should show Account Number, Billing Name, and Installation Address.
                    </p>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      required
                      onChange={(e) => setPortFile(e.target.files?.[0] || null)}
                      className="w-full rounded-lg border border-white/15 bg-white/5 px-4 py-3 text-sm text-white/65 file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:bg-blue-600 file:text-white file:font-semibold file:cursor-pointer hover:file:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {portFile && <p className="text-xs text-green-400 mt-1">Selected: {portFile.name}</p>}
                  </div>
                </section>

                <section className="space-y-4 rounded-xl border border-white/10 bg-white/5 p-5">
                  <h3 className="text-lg font-bold text-white">Customer Authorization</h3>
                  <div className="text-sm text-white/65 space-y-3 leading-relaxed">
                    <p>
                      By submitting this form, I, the undersigned, authorize SignalWire, Inc. (SignalWire) to act on my
                      behalf to make the necessary changes to my current business phone service to port the phone
                      number(s) listed above, including porting/disconnecting these phone number(s). I have been advised
                      by SignalWire that although all effort is made to coordinate a prompt conversion, local number
                      porting may result in a minor disruption in my local and/or long distance services. I have the
                      authority to change the phone service provider of the number(s) to be ported and I am also an
                      authorized user on the associated SignalWire account.
                    </p>
                    <p className="text-xs text-white/45">
                      NOTE: You must activate your SignalWire service and submit the completed form before the port
                      request can be initiated. It can take up to 15 business days from the date SignalWire receives your
                      completed Number Port Request form to process the request. During this period, you must maintain
                      your phone service with your previous provider. If service does not remain active with your
                      previous phone service provider until the port request is completed, your request could be
                      cancelled or denied by your previous phone service provider.
                    </p>
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="authorizationAccepted"
                      checked={portForm.authorizationAccepted}
                      onChange={handlePortFormChange}
                      required
                      className="mt-1 h-4 w-4 rounded border-white/30 bg-white/5 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-white/80">
                      I have read and agree to the authorization above. I am the owner / authorized user of the account
                      from the previous phone provider. *
                    </span>
                  </label>

                  <div>
                    <label className={labelClass}>Authorized Signature — type your full legal name *</label>
                    <p className={hintClass}>
                      Owner of account from previous phone provider. We create your electronic signature from this name.
                    </p>
                    <input
                      name="typedSignatureName"
                      value={portForm.typedSignatureName}
                      onChange={handlePortFormChange}
                      required
                      maxLength={160}
                      autoComplete="name"
                      className={fieldClass}
                      placeholder="e.g. Jordan A. Smith"
                    />
                    <div className="mt-3 rounded-xl border border-dashed border-blue-400/30 bg-white p-3">
                      <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
                        Signature preview
                      </p>
                      <canvas
                        ref={signatureCanvasRef}
                        className="w-full h-[120px] rounded-lg bg-[#f8fbff]"
                        aria-label="Electronic signature preview"
                      />
                    </div>
                  </div>

                  <div className="max-w-xs">
                    <label className={labelClass}>Date *</label>
                    <input
                      type="date"
                      name="signatureDate"
                      value={portForm.signatureDate}
                      onChange={handlePortFormChange}
                      required
                      className={`${fieldClass} [color-scheme:dark]`}
                    />
                  </div>
                </section>

                <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                  <button
                    type="button"
                    onClick={() => setPortModalOpen(false)}
                    className="px-6 py-3 rounded-lg border border-white/15 text-white/80 font-semibold hover:bg-white/10 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={portFormSubmitting}
                    className="px-6 py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-500 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {portFormSubmitting ? 'Sending...' : 'Submit Local TN LOA'}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default PhoneService;
